import Icon from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Trigger = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    padding: 0 1px 3px;
    color: inherit;
`;

const PortalContainer = styled.div`
    position: fixed;
    width: 220px;
    display: inline-flex;
    justify-content: center;
    z-index: 9999;
    pointer-events: none;
`;

const Body = styled.div`
    display: inline-block;
    padding: 10px;
    text-align: center;
    min-width: 100px;
    border-radius: 5px;
    background: ${getColor("common.backgroundContrast")};
    color: ${getColor("common.background")};
    font-size: 13px;
    line-height: 18px;
    pointer-events: auto;
`;

const ArrowUp = styled.div`
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent ${getColor("common.backgroundContrast")} transparent;
`;

const IconWrapper = styled.span`
    display: inline-flex;
    position: relative;
    top: 0.125em;
`;

interface Props {
    text: string;
    triggerAltText?: string;
}

const GAP = 11;

const PortalTooltip: React.FC<Props> = ({ text, triggerAltText = "more info" }) => {
    const [visible, setVisible] = React.useState(false);
    const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const updateCoords = React.useCallback(() => {
        if (!triggerRef.current) {
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({
            top: rect.bottom + GAP,
            left: rect.left + rect.width / 2,
        });
    }, []);

    const open = React.useCallback(
        (event: React.SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();
            updateCoords();
            setVisible(true);
        },
        [updateCoords],
    );

    const close = React.useCallback(() => {
        setVisible(false);
    }, []);

    React.useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const handleMouseDown = (event: MouseEvent) => {
            if (!triggerRef.current?.contains(event.target as Node)) {
                close();
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                close();
            }
        };
        const reposition = () => updateCoords();

        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);

        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [visible, close, updateCoords]);

    const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            open(event);
        }
    };

    return (
        <>
            <Trigger
                ref={triggerRef}
                onClick={open}
                onKeyDown={handleTriggerKeyDown}
                onBlur={close}
                type="button"
                data-test-selector="tooltip_OpenTooltip"
            >
                <IconWrapper>
                    <Icon src={HelpCircle} size={18} color="text.accent" />
                </IconWrapper>
                <VisuallyHidden>{triggerAltText}</VisuallyHidden>
            </Trigger>
            {visible &&
                coords &&
                typeof document !== "undefined" &&
                createPortal(
                    <PortalContainer
                        style={{ top: coords.top, left: coords.left, transform: "translateX(-50%)" }}
                        data-id="tooltipContainer"
                        aria-live="polite"
                    >
                        <ArrowUp />
                        <Body data-id="tooltipBody" data-test-selector="toolTip">
                            {text}
                        </Body>
                    </PortalContainer>,
                    document.body,
                )}
        </>
    );
};

export default PortalTooltip;
